import { type SearchCriteria, usePagination, type PaginationProps } from 'hooks/use-pagination'
import DataTable from 'Components/Table/DataTable'
import { Column, ColumnSort, ColumnType, type RowData } from 'Components/Table/DataTable/DataTable.model'
import Stack from '@mui/material/Stack/Stack'
import { ProbabilityChip } from 'Components/DuplicateHandling/ProbabilityChip'
import Avatar from '@mui/material/Avatar'
import S3Avatar from 'Components/S3Image/S3Avatar'
import { BirthdayToAgeInYearsAndMonths, FormatDateValue, SpaceBeforeCapitals } from 'core/Utilities'
import { type DuplicateListSearchCriteria } from 'Pages/DuplicateHandling/DuplicateListSearchOptions'
import { useEffect } from 'react'
import { ResolvedChip } from 'Components/DuplicateHandling/ResolvedChip'
import { type ChildDuplicate } from 'slices/duplicateChildSlice'
import { DuplicateStatusChip } from './DuplicateStatusChip'

export interface DuplicateItem {
  duplicateID: number
  childID: number
  childName: string
  imageKey?: string
  probability: string
  caregiver?: {
    caregiverName: string
    caregiverPhone: string
    caregiverEmail: string
  }
  entryDate: Date
  birthDate: Date
  isUnborn: boolean
  source: string
  isResolved: boolean
  accessGranted: boolean
  wasChildCreated?: boolean
}

interface Props {
  name: string
  duplicateList: DuplicateItem[]
  searchCriteria: DuplicateListSearchCriteria
  showISD: boolean
  showAllColumns?: boolean
  rowClick: (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, row: RowData) => Promise<void>
}

export function DuplicateTable (props: Props): JSX.Element {
  const { name, duplicateList, searchCriteria, showAllColumns, rowClick } = props

  const paginationProps: PaginationProps<RowData> = {
    rows: duplicateList,
    initialSize: 5,
    pageSizeSteps: [5, 10]
  }
  const pagination = usePagination(paginationProps)

  useEffect(() => {
    const fieldArray: SearchCriteria[] = []
    if (searchCriteria.childName !== '') {
      fieldArray.push({
        field: 'childName',
        value: searchCriteria.childName
      })
    }
    if (searchCriteria.caregiverName !== '') {
      fieldArray.push({
        field: 'caregiver',
        value: searchCriteria.caregiverName
      })
    }
    if (searchCriteria.source !== 'All') {
      fieldArray.push({
        field: 'source',
        value: searchCriteria.source
      })
    }
    if (searchCriteria.isResolved !== 'All') {
      fieldArray.push({
        field: 'isResolved',
        value: searchCriteria.isResolved === 'Y' ? 'true' : 'false'
      })
    }
    if (searchCriteria.isdID !== null) {
      fieldArray.push({
        field: 'isdid',
        value: searchCriteria.isdID.toString()
      })
    }
    pagination.setSearchFields(fieldArray)
  }, [searchCriteria])

  const caregiverColumn = new Column('caregiver', 'Caregiver Contact', ColumnType.CUSTOM)
  caregiverColumn.customCellGenerator = row => {
    return row.caregiver == null
      ? <div>Not Available</div>
      : <Stack direction='column'>
        <span>{row.caregiver.caregiverName}</span>
        <span>{row.caregiver.caregiverPhone}</span>
        <span><a href={`mailto:${row.caregiver.caregiverEmail as string}`}>{row.caregiver.caregiverEmail}</a></span>
      </Stack>
  }
  const probabilityColumn = new Column('probability', 'Probability', ColumnType.CUSTOM)
  probabilityColumn.customCellGenerator = row => {
    return <ProbabilityChip dupeData={row as ChildDuplicate} acceptanceThreshold={undefined} />
  }
  const nameColumn = new Column('childName', 'Student Name', ColumnType.CUSTOM)
  nameColumn.customCellGenerator = row => {
    return <div style={{ whiteSpace: 'nowrap' }}>
      {row.imageKey == null
        ? <Avatar sx={{ backgroundColor: '#7136BC', display: 'inline-grid' }}>{String(row.childName)[0]}</Avatar>
        : <S3Avatar imageKey={row.imageKey} height='50px' width='50px' style={{ display: 'inline-grid' }} />}
      <span style={{ whiteSpace: 'nowrap', paddingLeft: '5px' }}>{row.childName}</span>
    </div>
  }
  const dateColumn = new Column('entryDate', 'Created', ColumnType.CUSTOM)
  dateColumn.customCellGenerator = row => {
    return <div style={{ whiteSpace: 'nowrap' }}>
      {FormatDateValue(row.entryDate)}
    </div>
  }
  const ageColumn = new Column('birthDate', 'Age', ColumnType.CUSTOM)
  ageColumn.customCellGenerator = row => {
    return <div style={{ whiteSpace: 'nowrap' }}>
      {BirthdayToAgeInYearsAndMonths(row.birthDate, row.isUnborn)}
    </div>
  }
  const sourceColumn = new Column('source', 'Source', ColumnType.CUSTOM)
  sourceColumn.customCellGenerator = row => {
    let sourceName = ''
    switch (row.source) {
      case 'Child':
        sourceName = 'Child Creation'
        break
      case 'Sibling':
        sourceName = 'Sibling Creation'
        break
      default:
        sourceName = SpaceBeforeCapitals(row.source)
    }

    return <div style={{ whiteSpace: 'nowrap' }}>
      {sourceName}
    </div>
  }
  const resolvedColumn = new Column('isResolved', 'Resolved?', ColumnType.CUSTOM)
  resolvedColumn.customCellGenerator = row => {
    return <ResolvedChip isResolved={row.isResolved} wasChildCreated={row.wasChildCreated} />
  }
  const accessGrantedColumn = new Column('accessGranted', 'Access', ColumnType.CUSTOM)
  accessGrantedColumn.customCellGenerator = row => {
    if (!row.isResolved) { return <></> }
    const status = row.accessGranted ? 'Approved' : 'Declined'
    return <DuplicateStatusChip status={status} resolutionReason={null} />
  }
  const isdColumn = new Column('isd', 'ISD')

  const columns = showAllColumns === true
    ? [nameColumn, dateColumn, probabilityColumn, ageColumn, caregiverColumn, sourceColumn, resolvedColumn, accessGrantedColumn]
    : [nameColumn, dateColumn, probabilityColumn, caregiverColumn]
  if (props.showISD) {
    columns.push(isdColumn)
  }

  return <DataTable
    name={name}
    data-testid={name}
    columns={columns}
    rows={pagination.internalRows}
    page={pagination.page}
    hasCheckbox={false}
    onPageChange={pagination.handleChangePage}
    onRowClick={rowClick}
    totalRecords={pagination.recordCount}
    onSortChange={(col, sorts) => {
      pagination.onSortChange(col, sorts[0].order)
    }}
    onRowsPerPageChange={pagination.handleChangeRowsPerPage}
    initialColumnSorts={[new ColumnSort('childName')]}
    loading={false}
  />
}
